import React, { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { getAPIBaseURL, getAdmin } from "../../services/helpers"
import { getData } from "../../services/request"

const AdminSystemUsers = () => {
  const [admins, setAdmins] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [query, setQuery] = useState("")
  const currentAdmin = getAdmin()
  const isSuperAdmin = currentAdmin?.role === 'SUPER_ADMIN' || currentAdmin?.role === 'SUPER_MODERATOR'

  const fetchAdmins = async () => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/v1/admins`
    const params = { limit: 200, skip: 0 }
    const res = await getData(url, {}, params)
    setLoading(false)
    if(res.error == ""){
      if(res.result.status === 200){
        setAdmins(res.result.data?.data || [])
      }else{
        setError(res.result.data?.message || 'Failed to load admins')
      }
    }else{
      setError(res.result?.data?.message || 'Network error')
    }
  }

  useEffect(()=>{ fetchAdmins() }, [])

  const filtered = useMemo(()=>{
    const q = query.trim().toLowerCase()
    if(!q) return admins
    return admins.filter(a => (
      (a.name || '').toLowerCase().includes(q) ||
      (a.username || '').toLowerCase().includes(q) ||
      (a.phoneNumber || '').toString().includes(q) ||
      (a.role || '').toLowerCase().includes(q)
    ))
  }, [admins, query])

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleString() } catch(e){ return iso }
  }

  return (
    <section className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Users</h1>
          <p className="text-sm text-gray-600">Manage administrators and system users</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={query}
            onChange={(e)=>setQuery(e.target.value)}
            placeholder="Search admins (name, username, phone, role)"
            className="w-72 max-w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
          />
          {isSuperAdmin && (
            <Link
              to="/admin/app/system-users/new"
              className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900"
            >
              Create Admin
            </Link>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-6 text-sm text-gray-500">Loading...</div>
        ) : error ? (
          <div className="p-6 text-sm text-red-600">{error}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 font-semibold text-gray-700">Name</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Username</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Phone</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Role</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Created</th>
                  {isSuperAdmin && <th className="px-4 py-3"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((admin) => (
                  <tr key={admin._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      {isSuperAdmin ? (
                        <Link to={`/admin/app/system-users/${admin._id}`} className="font-medium text-gray-900 hover:underline">
                          {admin.name}
                        </Link>
                      ) : (
                        <span className="font-medium text-gray-900">{admin.name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{admin.username}</td>
                    <td className="px-4 py-3 text-gray-700">{admin.phoneNumber}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                        admin.role === 'SUPER_ADMIN' ? 'bg-purple-100 text-purple-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {admin.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                        admin.status === 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {admin.status === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(admin.createdAt)}</td>
                    {isSuperAdmin && (
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/admin/app/system-users/${admin._id}`}
                          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          {admin.role === 'SUPER_ADMIN' ? 'View' : 'Edit'}
                        </Link>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">No matching admins</div>
            ) : null}
          </div>
        )}
      </div>
    </section>
  )
}

export default AdminSystemUsers
